import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { fromNow } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, StickyNote, Trash2 } from "lucide-react";
import { Sparkles, Loader2 } from "lucide-react";

export default function Notes() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "" });
  const [aiLoading, setAiLoading] = useState(false);

  const { data = [], isLoading } = useQuery({ queryKey: ["notes"], queryFn: async () => { const d = (await api.get("/notes")).data; return Array.isArray(d) ? d : []; } });

  const openNew = () => { setEditId(null); setForm({ title: "", content: "" }); setOpen(true); };
  const openEdit = (n) => { setEditId(n.id); setForm({ title: n.title, content: n.content || "" }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Başlık gerekli"); return; }
    try {
      if (editId) await api.patch(`/notes/${editId}`, form);
      else await api.post("/notes", form);
      toast.success(editId ? "Not güncellendi" : "Not eklendi");
      qc.invalidateQueries({ queryKey: ["notes"] }); setOpen(false); setForm({ title: "", content: "" }); setEditId(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/notes/${id}`); toast.success("Not silindi"); qc.invalidateQueries({ queryKey: ["notes"] }); };

  const summarize = async () => {
    if (!form.content.trim()) { toast.error("Özetlenecek metin girin"); return; }
    setAiLoading(true);
    try { const { data } = await api.post("/ai/summarize", { text: form.content }); setForm((f) => ({ ...f, content: f.content + "\n\n--- AI Özeti ---\n" + data.summary })); toast.success("Özet eklendi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  return (
    <div>
      <PageHeader title="Notlar" subtitle="Toplantı notları ve hızlı notlar"
        actions={<Button onClick={openNew} data-testid="new-note-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Not</Button>} />

      {isLoading ? <ListSkeleton /> : data.length === 0 ? <EmptyState icon={StickyNote} title="Not yok" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((n) => (
            <Card key={n.id} className="p-4 group cursor-pointer hover:shadow-md transition-all" data-testid={`note-${n.id}`} onClick={() => openEdit(n)}>
              <div className="flex items-start justify-between">
                <h3 className="font-semibold">{n.title}</h3>
                <AlertDialog>
                  <AlertDialogTrigger asChild><button onClick={(e) => e.stopPropagation()} className="md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader><AlertDialogTitle>Notu sil</AlertDialogTitle><AlertDialogDescription>Bu not kalıcı olarak silinsin mi?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={() => remove(n.id)} data-testid="confirm-delete-button">Sil</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap line-clamp-6">{n.content}</p>
              <p className="text-[10px] text-muted-foreground mt-3">{fromNow(n.updated_at || n.created_at)}</p>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader><DialogTitle>{editId ? "Notu Düzenle" : "Yeni Not"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="note-title-input" /></div>
            <div>
              <div className="flex items-center justify-between"><Label>İçerik</Label>
                <Button type="button" variant="ghost" size="sm" onClick={summarize} disabled={aiLoading} className="h-7 text-indigo-600" data-testid="note-ai-summarize">
                  {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI Özetle
                </Button>
              </div>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6} className="mt-1.5" data-testid="note-content-input" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button onClick={save} data-testid="note-save-button">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
